import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Icons,
} from '@commenda/kakashi';

import { IServiceAuditReportSectionView } from '../helpers/ServiceAuditReportHelper';
import { ServiceAuditFindingCard } from './ServiceAuditFindingCard';
import { ServiceAuditPreviousOutcomeIcon } from './ServiceAuditPreviousOutcomeIcon';

interface IServiceAuditReportSectionProps {
  section: IServiceAuditReportSectionView;
  onFeedbackSaved: (() => Promise<unknown>) | null;
}

/** One accordion section of the audit report: its finding cards, the checks passed, or last round's findings. */
export const ServiceAuditReportSection = ({
  section,
  onFeedbackSaved,
}: IServiceAuditReportSectionProps) => {
  const {
    config,
    findings,
    checksPassed,
    previousOutcomes,
    itemCount,
    progressLabel,
  } = section;

  return (
    <AccordionItem
      value={config.key}
      className="rounded-xl border border-border-primary px-xl"
    >
      <AccordionTrigger>
        <span className="flex flex-wrap items-center gap-md">
          <Badge size="sm" variant={config.variant}>
            {config.label}
          </Badge>
          <span className="txt-small-medium text-text-primary">
            {itemCount}
          </span>
          {progressLabel && (
            <span className="txt-extra-small-regular text-text-tertiary">
              {progressLabel}
            </span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-xl pb-xl">
        <p className="txt-extra-small-regular text-text-tertiary">
          {config.subtitle}
        </p>
        {checksPassed.length > 0 && (
          <ul className="flex flex-col gap-md">
            {checksPassed.map((check) => (
              <li key={check} className="flex items-start gap-md">
                <Icons.Check className="size-4 shrink-0 text-text-success" />
                <span className="break-words txt-small-regular text-text-secondary">
                  {check}
                </span>
              </li>
            ))}
          </ul>
        )}
        {config.previousOutcomeList && previousOutcomes.length > 0 && (
          <ul className="flex flex-col gap-md">
            {previousOutcomes.map((outcome) => (
              <li key={outcome.key} className="flex items-start gap-md">
                <ServiceAuditPreviousOutcomeIcon
                  listKey={config.previousOutcomeList}
                />
                <span className="flex min-w-0 flex-col gap-xs">
                  <span className="break-words txt-small-medium text-text-primary">
                    {outcome.headline}
                  </span>
                  {outcome.detailLines.map((line) => (
                    <span
                      key={line.key}
                      className="break-words txt-small-regular text-text-secondary"
                    >
                      {line.text}
                    </span>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
        {findings.map((finding) => (
          <ServiceAuditFindingCard
            key={finding.id}
            finding={finding}
            onFeedbackSaved={onFeedbackSaved}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
};
